import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { DismissedArCollectionAction } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';

/**
 * Cached short-pay recommendation for a collection thread, plus regenerate and
 * dismissal actions. Auto-generated on short-pay thread creation; this hook
 * reads the cached result, lets the user trigger a fresh analysis, and lets the
 * user mute ("don't suggest this again") / unmute individual recommended
 * actions for the thread's customer.
 */
export function useArShortPayRecommendations(threadId: string) {
  const queryClient = useQueryClient();

  const queryOptions =
    orpc.accountsReceivable.arShortPayRecommendations.get.queryOptions({
      input: { threadId },
    });

  const query = useQuery(queryOptions);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });

  const regenerateMutation = useMutation(
    orpc.accountsReceivable.arShortPayRecommendations.regenerate.mutationOptions(
      { onSuccess: invalidate },
    ),
  );

  const dismissMutation = useMutation(
    orpc.accountsReceivable.arShortPayRecommendations.dismissAction.mutationOptions(
      { onSuccess: invalidate },
    ),
  );

  // Undismiss archives the underlying customer memory bank note via the shared
  // memory-bank delete endpoint; on success we refetch so the muted action
  // reappears in the recommendation list.
  const undismissMutation = useMutation(
    orpc.memoryBank.delete.mutationOptions({ onSuccess: invalidate }),
  );

  return {
    ...query,
    recommendation: query.data?.recommendation ?? null,
    generatedAt: query.data?.generatedAt ?? null,
    isStale: query.data?.isStale ?? false,
    hasCachedResult: query.data?.hasCachedResult ?? false,
    salesperson: query.data?.salesperson ?? null,
    dismissedActions: query.data?.dismissedActions ?? [],
    actionCatalog: query.data?.actionCatalog ?? [],
    regenerate: () => regenerateMutation.mutate({ threadId }),
    isRegenerating: regenerateMutation.isPending,
    dismissAction: (
      action: DismissedArCollectionAction['action'],
      reason?: string,
    ) => dismissMutation.mutate({ threadId, action, reason }),
    isDismissing: dismissMutation.isPending,
    undismissAction: (noteId: string) =>
      undismissMutation.mutate({ id: noteId }),
    isUndismissing: undismissMutation.isPending,
  };
}
